import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { api, apiError, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const probed = useRef(false);

  const load = useCallback(async () => {
    // The donor portal and the public onboarding form have no staff session by design.
    if (/^\/(donor|onboard)(\/|$)/.test(window.location.pathname)) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (probed.current) return;
    probed.current = true;
    load();
  }, [load]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.must_change_password) return { ok: false, firstPassword: data };
      if (data.mfa_required) return { ok: false, mfa: data };
      localStorage.setItem(TOKEN_KEY, data.access_token);
      await load();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const adoptSession = async (session) => {
    localStorage.setItem(TOKEN_KEY, session.access_token);
    await load();
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const can = (resource, action) => Boolean(user?.permissions?.[resource]?.includes(action));

  return (
    <AuthContext.Provider value={{ user, login, logout, adoptSession, refresh: load, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
